import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Zap, Mail, Clock, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const inputRefs = useRef([]);
  
  // Get email from location state or localStorage
  const email = location.state?.email || localStorage.getItem('signup_email');
  const organizationName = location.state?.organizationName || localStorage.getItem('signup_org_name');
  const organizationId = location.state?.organizationId || localStorage.getItem('signup_org_id');

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      toast.error("No email found. Please sign up again.");
      navigate('/signup');
    }
  }, [email, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          toast.error("Verification PIN expired. Please request a new one.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Format time left as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle PIN input
  const handlePinChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value.slice(-1); // Only take last character
    setPin(newPin);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setPin(pastedData.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  // Verify PIN
  const handleVerify = async () => {
    const pinValue = pin.join('');
    
    if (pinValue.length !== 6) {
      toast.error("Please enter all 6 digits");
      return;
    }

    if (timeLeft <= 0) {
      toast.error("PIN has expired. Please request a new one.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/verify-pin`, {
        email: email,
        pin: pinValue
      });

      if (response.data.success) {
        toast.success("Email verified successfully!");
        
        // Clear localStorage signup data
        localStorage.removeItem('signup_email');
        localStorage.removeItem('signup_org_name');
        localStorage.removeItem('signup_org_id');
        
        // Navigate to pending approval page
        navigate('/pending-approval', {
          state: {
            organization: response.data.organization
          }
        });
      }
    } catch (error) {
      console.error("Verification error:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend PIN
  const handleResendPin = async () => {
    setResending(true);
    try {
      const response = await axios.post(`${API_URL}/auth/resend-pin`, {
        email: email
      });

      if (response.data.success) {
        toast.success("New PIN sent! Check your email.");
        setTimeLeft(900); // Reset to 15 minutes (backend sends 15 min expiry)
        setPin(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("Resend error:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to resend PIN. Please try again.");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-lg shadow-xl">
        <CardHeader className="text-center pt-12 pb-6">
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-xl shadow-lg bg-blue-600">
              <Mail className="w-12 h-12 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold mb-2">
            Verify Your Email
          </CardTitle>
          <p className="text-gray-600">
            We sent a 6-digit code to
          </p>
          <p className="text-blue-600 font-semibold">
            {email}
          </p>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          {/* Timer */}
          <div className={`flex items-center justify-center gap-2 mb-6 p-3 rounded-lg ${
            timeLeft < 60 ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
          }`}>
            <Clock className="w-5 h-5" />
            <span className="font-mono text-lg font-bold">
              {formatTime(timeLeft)}
            </span>
            <span className="text-sm">remaining</span>
          </div>

          {/* PIN Input */}
          <div className="flex justify-center gap-2 mb-6">
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:outline-none transition-all"
              />
            ))}
          </div>

          {/* Verify Button */}
          <Button
            onClick={handleVerify}
            disabled={loading || pin.join('').length !== 6 || timeLeft <= 0}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6 mb-4"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify Email"
            )}
          </Button>

          {/* Resend Button */}
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              Didn't receive the code?
            </p>
            <Button
              variant="ghost"
              onClick={handleResendPin}
              disabled={resending}
              className="text-blue-600 hover:text-blue-700"
            >
              {resending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Resend PIN
                </>
              )}
            </Button>
          </div>

          {/* Change Email */}
          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm text-gray-600 mb-2">
              Wrong email address?
            </p>
            <Button
              variant="ghost"
              onClick={() => {
                localStorage.removeItem('signup_email');
                localStorage.removeItem('signup_org_name');
                localStorage.removeItem('signup_org_id');
                navigate('/signup');
              }}
              className="text-gray-600 hover:text-gray-900"
            >
              Change Email & Sign Up Again
            </Button>
          </div>
        </CardContent>

        <div className="text-center pb-6 text-xs text-gray-500">
          © {new Date().getFullYear()} BookingFlows. All rights reserved.
        </div>
      </Card>
    </div>
  );
};

export default VerifyEmail;
